import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { CheckCircleIcon } from '../../icons';
import { color, r, radius } from '../../theme/tokens';
import { logAnalyticsEvent } from '../../utils/analytics';
import { PAGE_PATH, TRIAL_BUTTON, TRIAL_HEADING, TRIAL_NOTE, TRIAL_PERKS } from './copy';

// The membership trial card under every checker result (STACK-588; v5.1
// states 7, 9 and 11; design doc §3.1, "The wall"). A trial start — a
// completed checkout returning to /profile?success=true — is the conversion
// the search campaign counts, so the button follows the normal path and
// never goes to /login: logged out, /register → /complete-registration
// (pricing) → checkout; signed in, /subscribe → checkout. The page renders
// no card for a member.

export const TRIAL_OUTCOME = { FLAGS: 'flags', CLEAN: 'clean', FLAG_OFF: 'flag_off' };

const Perk = ({ children }) => (
  <Box
    component="li"
    sx={{ display: 'flex', gap: r(10), alignItems: 'flex-start', fontSize: r(15), lineHeight: 1.45, color: color.textBody }}
  >
    <CheckCircleIcon aria-hidden="true" sx={{ flex: 'none', fontSize: r(16), mt: r(3), color: color.accent }} />
    {children}
  </Box>
);

/**
 * @param {Object} props
 * @param {'flags'|'clean'|'flag_off'} props.outcome - what the card sits under
 * @param {boolean} props.signedIn - picks the path: /subscribe signed in, /register logged out
 */
export default function CheckerTrialCard({ outcome, signedIn }) {
  const navigate = useNavigate();
  const shown = useRef(false);

  // Once per result screen: a new result mounts a new card.
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    logAnalyticsEvent('checker_trial_card_shown', { outcome });
  }, [outcome]);

  const start = () => {
    logAnalyticsEvent('checker_trial_clicked', { outcome, signed_in: signedIn });
    if (signedIn) {
      navigate('/subscribe', {
        state: { from: { pathname: PAGE_PATH }, source: 'interaction_checker' },
      });
    } else {
      navigate('/register');
    }
  };

  return (
    <Box
      component="section"
      aria-labelledby="checker-trial-heading"
      data-testid="checker-trial-card"
      sx={{
        mt: r(8),
        backgroundColor: color.paper,
        border: `${r(1)} solid ${color.line}`,
        borderRadius: radius.lg,
        // Phone: stacked, the button full width. Desktop (state 11): the
        // heading and lines left, the button and its note right.
        px: { xs: r(18), md: r(24) },
        py: { xs: r(20), md: r(24) },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'center' },
        gap: { xs: r(14), md: r(32) },
      }}
    >
      <Box sx={{ flex: { md: 1 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: { xs: r(14), md: r(10) } }}>
        <Typography
          id="checker-trial-heading"
          component="h2"
          sx={{ m: 0, fontSize: r(20), fontWeight: 800, lineHeight: 1.25, letterSpacing: '-0.02em', color: color.ink }}
        >
          {TRIAL_HEADING}
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: r(8) }}>
          {TRIAL_PERKS.map((perk) => <Perk key={perk}>{perk}</Perk>)}
        </Box>
      </Box>
      <Box sx={{ flex: 'none', display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'center' }, gap: r(8) }}>
        <Button
          variant="contained"
          disableElevation
          onClick={start}
          sx={{
            minHeight: r(52),
            minWidth: { md: r(160) },
            px: r(24),
            borderRadius: radius.md,
            backgroundColor: color.accent,
            color: color.accentInk,
            fontSize: r(16),
            fontWeight: 700,
            textTransform: 'none',
            '&:hover': { backgroundColor: color.accentHover },
          }}
        >
          {TRIAL_BUTTON}
        </Button>
        <Typography sx={{ m: 0, fontSize: r(13), color: color.inkSoft, textAlign: 'center' }}>{TRIAL_NOTE}</Typography>
      </Box>
    </Box>
  );
}
